// src/App.jsx
import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import api from "./api";

// 🎨 Global stiller
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/components.css";
import "./styles/site-theme.css";
import "./styles/utilities.css";

// 🧱 Layout
import TakofyBackButton from "./components/TakofyBackButton";
import TakofyMobileShell from "./components/TakofyMobileShell";
import PredictionAuth from "./pages/PredictionAuth";
import TakofyLegal from "./pages/TakofyLegal";
import FootballPlayerLegacyRedirect from "./components/FootballPlayerLegacyRedirect";

// 🔒 Auth guard (COOKIE BASED)
import RequireAuth from "./routes/RequireAuth";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toast.css";
import "./styles/takofy-professional-mobile.css";
import { createPortal } from "react-dom";
import CookieBanner from "./components/CookieBanner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useDefaultCompany } from "./contexts/DefaultCompanyContext";
import { DefaultCompanyProvider } from "./contexts/DefaultCompanyContext";
import { PublicSiteProvider, usePublicSite } from "./contexts/PublicSiteContext";
import { resolveSiteThemeKey } from "./utils/siteTheme";
import { resolvePublicLogoUrl } from "./utils/logo";
import { getTakofyPushStatus, subscribeToTakofyPush } from "./utils/webPush";
import { activateTakofyNativePush, isNativePushSupported } from "./utils/nativePush";

// Takofy and the accommodation sites share this bundle. Keep the much heavier
// accommodation UI out of Takofy's initial download by loading it per route.
const Header = React.lazy(() => import("./components/Header"));
const Footer = React.lazy(() => import("./components/Footer"));
const Home = React.lazy(() => import("./pages/Home"));
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const Kvkk = React.lazy(() => import("./pages/Kvkk"));
const SapancaAttractions = React.lazy(() => import("./pages/SapancaAttractions"));
const HostLayout = React.lazy(() => import("./layouts/HostLayout"));
const HostDashboard = React.lazy(() => import("./pages/HostDashboard"));
const HostHouses = React.lazy(() => import("./pages/HostHouses"));
const HostReports = React.lazy(() => import("./pages/HostReports"));
const HostFinance = React.lazy(() => import("./pages/HostFinance"));
const HostProfile = React.lazy(() => import("./pages/HostProfile"));
const HostNotifications = React.lazy(() => import("./pages/HostNotifications"));
const HostGoogleAds = React.lazy(() => import("./pages/HostGoogleAds"));
const HostReservations = React.lazy(() => import("./pages/HostReservations"));
const HostTemplates = React.lazy(() => import("./pages/HostTemplates"));
const HostLogoSettings = React.lazy(() => import("./pages/HostLogoSettings"));
const HostSiteSettings = React.lazy(() => import("./pages/HostSiteSettings"));
const HostUsers = React.lazy(() => import("./pages/HostUsers"));
const CalendarPage = React.lazy(() => import("./pages/CalendarPage"));
const WizardContainer = React.lazy(() => import("./components/house-wizard/WizardContainer"));
const HostEditHouse = React.lazy(() => import("./pages/HostEditHouse"));

const PredictionLeagueHome = React.lazy(() => import("./pages/PredictionLeagueHome"));
const FootballMatchDetail = React.lazy(() => import("./pages/FootballMatchDetail"));
const FootballLeagueSeason = React.lazy(() => import("./pages/FootballLeagueSeason"));
const FootballCompetitions = React.lazy(() => import("./pages/FootballCompetitions"));
const FootballMatchComparison = React.lazy(() => import("./pages/FootballMatchComparison"));
const PredictionProfile = React.lazy(() => import("./pages/PredictionProfile"));
const FootballAdmin = React.lazy(() => import("./pages/FootballAdmin"));
const CardFantasy = React.lazy(() => import("./pages/CardFantasy"));
const FootballTeamDetail = React.lazy(() => import("./pages/FootballTeamDetail"));
const FootballPlayerProfile = React.lazy(() => import("./pages/FootballPlayerProfile"));
const FootballPlayerPositionMatches = React.lazy(() => import("./pages/FootballPlayerPositionMatches"));
const FootballPlayerComparison = React.lazy(() => import("./pages/FootballPlayerComparison"));
const FootballPlayerLeaders = React.lazy(() => import("./pages/FootballPlayerLeaders"));
const FootballPowerMovers = React.lazy(() => import("./pages/FootballPowerMovers"));
const UefaRankings = React.lazy(() => import("./pages/UefaRankings"));
const TakofyGames = React.lazy(() => import("./pages/TakofyGames"));

const SW_VERSION = "2026-09-01-2";

function shouldUsePredictionLeague(hostname) {
  const isNativeTakofy = typeof window !== "undefined" &&
    typeof window.Capacitor?.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform();
  if (isNativeTakofy) return true;
  const host = String(hostname || "").toLowerCase();
  return host === "takofy.com" || host === "www.takofy.com";
}

function AppInner() {
  const { pathname } = useLocation();
  const { company } = usePublicSite();
  const { defaultCompany } = useDefaultCompany();
  const { user } = useAuth();
  const swRegistrationRef = useRef(null);
  const updateToastIdRef = useRef(null);
  const didReloadForSwRef = useRef(false);
  const isPredictionLeague = shouldUsePredictionLeague(window.location.hostname);
  const isTakofyLegal = isPredictionLeague && ["/kurallar","/privacy-policy","/kvkk"].includes(pathname);

  useEffect(()=>{
    if(!isPredictionLeague)return undefined;
    const key="takofy_presence_id";
    let visitorId=localStorage.getItem(key);
    if(!visitorId){visitorId=(window.crypto?.randomUUID?.()||`${Date.now()}_${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9_-]/g,"");localStorage.setItem(key,visitorId);}
    const ping=()=>api.post("/football/presence",{visitorId,path:`${window.location.pathname}${window.location.search}`}).catch(()=>{});
    ping();const timer=window.setInterval(ping,60*1000);window.addEventListener("focus",ping);document.addEventListener("visibilitychange",ping);
    return()=>{window.clearInterval(timer);window.removeEventListener("focus",ping);document.removeEventListener("visibilitychange",ping);};
  },[isPredictionLeague,pathname,user?.id]);

  useEffect(() => {
    if (!isPredictionLeague || !user?.id || !isNativePushSupported()) return undefined;
    let listener = null;
    let cancelled = false;
    activateTakofyNativePush(api, (notification) => {
      const title = notification?.title || "Takofy bildirimi";
      const body = notification?.body || "Maç güncellemesi var.";
      toast.info(<div className="takofy-push-toast__content"><strong>{title}</strong><span>{body}</span></div>, {
        className: "takofy-push-toast",
        icon: false,
        toastId: `takofy-native-${notification?.data?.notificationId || notification?.id || title}`,
      });
    }).then((value) => {
      if (cancelled) value?.remove?.();
      else listener = value;
    }).catch((error) => console.warn("Takofy native push başlatılamadı", error));
    return () => { cancelled = true; listener?.remove?.(); };
  }, [isPredictionLeague, user?.id]);

  useEffect(() => {
    if (!isPredictionLeague || !user?.id) return undefined;
    if (isNativePushSupported()) return undefined;
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return undefined;

    const sessionKey = `takofy_push_reminder_${user.id}`;
    if (sessionStorage.getItem(sessionKey) === "shown") return undefined;
    let cancelled = false;

    const showReminder = async () => {
      try {
        const status = await getTakofyPushStatus(api);
        if (cancelled || (Notification.permission === "granted" && Number(status?.active || 0) > 0)) return;
        sessionStorage.setItem(sessionKey, "shown");

        toast.info(
          ({ closeToast }) => (
            <div className="takofy-push-reminder">
              <strong>🔔 Maç bildirimlerini aç</strong>
              <span>Takip ettiğin maçların gol, kadro ve sonuç haberlerini kaçırma.</span>
              <button
                type="button"
                onClick={async () => {
                  if (Notification.permission === "denied") {
                    closeToast?.();
                    window.location.assign("/profil");
                    return;
                  }
                  try {
                    await subscribeToTakofyPush(api, "Takofy uygulaması");
                    closeToast?.();
                    toast.success("Bildirimler açıldı.", { autoClose: 3000 });
                  } catch (error) {
                    toast.error(error?.message || "Bildirim izni açılamadı.");
                  }
                }}
              >
                {Notification.permission === "denied" ? "Ayarlara git" : "Bildirimleri aç"}
              </button>
            </div>
          ),
          {
            className: "takofy-push-toast takofy-push-reminder-toast",
            icon: false,
            autoClose: false,
            closeOnClick: false,
            toastId: `takofy-push-reminder-${user.id}`,
          },
        );
      } catch {}
    };

    const timer = window.setTimeout(showReminder, 1400);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [isPredictionLeague, user?.id]);

  useEffect(() => {
    if (isPredictionLeague) {
      document.title = "Takofy";
      return;
    }

    const companyName = String(defaultCompany?.Name || company?.Name || "Lakeview Villa Resort").trim();
    const hostTitles = {
      "/": isPredictionLeague ? "Süper Lig Tahmin Ligi" : "Ana Sayfa",
      "/host": "Ev Sahibi Paneli",
      "/host/calendar": "Takvim",
      "/host/reservations": "Rezervasyon Yonetimi",
      "/host/profile": "Profil",
      "/host/notifications": "Bildirim Merkezi",
      "/host/houses": "Evler",
      "/host/houses/new": "Yeni Ev",
      "/host/reports": "Raporlar",
      "/host/finance": "Finans",
      "/host/templates": "Onam Formu Ayarlari",
      "/host/logo": "Logo Ayarlari",
      "/host/site-settings": "Site Ayarlari",
      "/host/users": "Kullanicilar",
      "/login": "Giris Yap",
      "/hesabim": "Giris Yap",
      "/register": "Kayit Ol",
      "/forgot": "Sifremi Unuttum",
      "/reset": "Sifre Sifirla",
      "/privacy-policy": "Gizlilik Politikasi",
      "/kvkk": "KVKK",
      "/sapanca-gezilecek-yerler": "Sapanca Rehberi",
      "/site-home": "Ana Sayfa",
    };

    const pageTitle =
      hostTitles[pathname] ||
      (pathname.startsWith("/host/houses/") ? "Ev Duzenle" : "");

    document.title = pageTitle ? `${companyName} | ${pageTitle}` : companyName;
  }, [company?.Name, defaultCompany?.Name, isPredictionLeague, pathname]);

  useEffect(() => {
    if (isPredictionLeague) {
      document
        .querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']")
        .forEach((node) => node.setAttribute("href", "/brand/takofy-192.png"));
      return;
    }

    const rawLogo = String(company?.LogoUrl || "").trim();
    if (!rawLogo) return;

    const nextHref = resolvePublicLogoUrl(rawLogo);
    document
      .querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']")
      .forEach((node) => {
        node.setAttribute("href", nextHref);
      });
  }, [company?.LogoUrl, isPredictionLeague]);

  useEffect(() => {
    const themeKey = resolveSiteThemeKey(window.location.hostname);
    document.documentElement.setAttribute("data-site-theme", themeKey);
    return () => {
      document.documentElement.removeAttribute("data-site-theme");
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onSwMessage = (event) => {
      const type = event?.data?.type;
      if (type === "TAKOFY_PUSH_RECEIVED") {
        const p = event?.data?.payload || {};
        toast.info(
          <div className="takofy-push-toast__content">
            <strong className={String(p?.title || "").startsWith("GOL!") ? "is-goal" : ""}>
              {String(p?.title || "").startsWith("GOL!") ? "⚽ " : ""}
              {p?.title || "Takofy"}
            </strong>
            {p?.body ? <span>{p.body}</span> : null}
          </div>,
          {
            className: "takofy-push-toast",
            icon: false,
            autoClose: 6500,
            toastId: p?.data?.notificationId
              ? `takofy-push-${p.data.notificationId}`
              : undefined,
          },
        );
        window.dispatchEvent(new CustomEvent("takofy-push-received", { detail: p }));
        return;
      }
      if (type !== "HOST_PUSH_RECEIVED") return;

      const p = event?.data?.payload || {};
      const reservationId = Number(p?.data?.reservationId || 0);
      const text = `${p?.title || "Yeni rezervasyon"}${p?.body ? `\n${p.body}` : ""}`;

      toast.info(text, {
        toastId: reservationId > 0 ? `host-push-${reservationId}` : undefined,
        autoClose: 5500,
      });
    };

    navigator.serviceWorker.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onSwMessage);
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    let cancelled = false;

    const promptForUpdate = (registration) => {
      const waitingWorker = registration?.waiting;
      if (!waitingWorker) return;
      if (updateToastIdRef.current && toast.isActive(updateToastIdRef.current)) return;
      const companyName = String(defaultCompany?.Name || company?.Name || "Uygulama").trim();

      updateToastIdRef.current = toast.info(
        ({ closeToast }) => (
          <div className="app-update-toast">
            <div className="app-update-toast__title">Yeni surum hazir</div>
            <div className="app-update-toast__text">
              {companyName} uygulamasi guncellendi.
              <br />
              Son degisiklikleri almak icin yenileyebilirsiniz.
            </div>
            <div className="app-update-toast__actions">
              <button
                type="button"
                className="app-update-toast__btn"
                onClick={() => {
                  waitingWorker.postMessage({ type: "SKIP_WAITING" });
                  closeToast?.();
                }}
              >
                Simdi Yenile
              </button>
            </div>
          </div>
        ),
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          toastId: "sw-update-ready",
        }
      );
    };

    const watchRegistration = (registration) => {
      if (!registration) return;
      if (registration.waiting) promptForUpdate(registration);

      registration.addEventListener("updatefound", () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;
        installingWorker.addEventListener("statechange", () => {
          if (
            installingWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            promptForUpdate(registration);
          }
        });
      });
    };

    const registerAndWatch = async () => {
      try {
        const registration = await navigator.serviceWorker.register(`/sw.js?v=${SW_VERSION}`);
        if (cancelled) return;
        swRegistrationRef.current = registration;
        watchRegistration(registration);
      } catch (err) {
        console.warn("Service worker register warning:", err?.message || err);
      }
    };

    const onControllerChange = () => {
      if (didReloadForSwRef.current) return;
      didReloadForSwRef.current = true;
      window.location.reload();
    };

    void registerAndWatch();
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const intervalId = window.setInterval(() => {
      swRegistrationRef.current?.update?.();
    }, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, [company?.Name, defaultCompany?.Name]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    const getSelector = (el) => {
      if (el.id) return `#${el.id}`;
      const cls = (el.className || "")
        .toString()
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 3)
        .join(".");
      return `${el.tagName.toLowerCase()}${cls ? "." + cls : ""}`;
    };

    const reportOverflow = () => {
      const viewport = window.innerWidth;
      const nodes = Array.from(document.querySelectorAll("body *"));
      const offenders = [];

      for (const el of nodes) {
        const r = el.getBoundingClientRect();
        const overflowRight = r.right - viewport;
        const overflowLeft = Math.abs(Math.min(0, r.left));
        const overflow = Math.max(overflowRight, overflowLeft);

        if (overflow > 1) {
          offenders.push({
            selector: getSelector(el),
            overflowPx: Math.round(overflow),
            left: Math.round(r.left),
            right: Math.round(r.right),
            width: Math.round(r.width),
          });
        }
      }

      if (offenders.length) {
        // eslint-disable-next-line no-console
        console.table(offenders.slice(0, 15));
      }
    };

    const t1 = setTimeout(reportOverflow, 400);
    const t2 = setTimeout(reportOverflow, 1200);
    window.addEventListener("resize", reportOverflow);
    window.__overflowReport = reportOverflow;

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", reportOverflow);
      delete window.__overflowReport;
    };
  }, [pathname]);

  const hideSiteChrome =
    (isPredictionLeague && (pathname === "/" || pathname.startsWith("/tahmin-manager") || pathname.startsWith("/maclar/") || pathname.startsWith("/vs/") || pathname === "/ligler" || pathname.startsWith("/ligler/") || pathname.startsWith("/takimlar/") || pathname.startsWith("/oyuncular/") || pathname === "/oyuncu-karsilastirma" || pathname === "/oyuncu-liderleri" || pathname === "/power-99-degisim" || pathname === "/siralamalar" || pathname === "/oyunlar" || pathname === "/profil" || pathname === "/futbol-admin" || pathname.startsWith("/fantezi"))) ||
    (isPredictionLeague && ["/login", "/register", "/forgot", "/reset", "/kurallar", "/privacy-policy", "/kvkk"].includes(pathname)) ||
    pathname === "/register" ||
    pathname === "/forgot" ||
    pathname === "/reset";

  return (
    <>
      <React.Suspense fallback={<main className="prediction-portal"><p className="portal-notice">Sayfa yükleniyor…</p></main>}>
      {!hideSiteChrome ? <Header /> : null}
      {isPredictionLeague && !isTakofyLegal && pathname !== "/" && !pathname.startsWith("/tahmin-manager") && !pathname.startsWith("/fantezi") ? <TakofyBackButton /> : null}

      {isPredictionLeague ? <TakofyMobileShell><React.Suspense fallback={<main className="prediction-portal"><p className="portal-notice">Takofy yükleniyor…</p></main>}><Routes>
        {/* 🌍 Public */}
        <Route path="/" element={isPredictionLeague ? <PredictionLeagueHome /> : <Home />} />
        <Route path="/tahmin-manager" element={isPredictionLeague ? <PredictionLeagueHome mode="manager" /> : <Navigate to="/" replace />} />
        <Route path="/tahmin-manager/liderlik" element={isPredictionLeague ? <Navigate to="/tahmin-manager?tab=leaderboard" replace /> : <Navigate to="/" replace />} />
        <Route path="/maclar/:matchId" element={isPredictionLeague ? <FootballMatchDetail /> : <Navigate to="/" replace />} />
        <Route path="/vs/:matchId" element={isPredictionLeague ? <FootballMatchComparison /> : <Navigate to="/" replace />} />
         <Route path="/ligler" element={isPredictionLeague ? <FootballCompetitions /> : <Navigate to="/" replace />} />
         <Route path="/fantezi/liderlik" element={isPredictionLeague ? <Navigate to="/fantezi?tab=leaderboard" replace /> : <Navigate to="/" replace />} />
        <Route path="/ligler/:competitionCode/:startYear" element={isPredictionLeague ? <FootballLeagueSeason /> : <Navigate to="/" replace />} />
        <Route path="/ligler/:competitionCode/:startYear/oyuncular/:playerId" element={isPredictionLeague ? <FootballPlayerLegacyRedirect /> : <Navigate to="/" replace />} />
        <Route path="/takimlar/:teamId" element={isPredictionLeague ? <FootballTeamDetail /> : <Navigate to="/" replace />} />
        <Route path="/takimlar/:teamId/:startYear" element={isPredictionLeague ? <FootballTeamDetail /> : <Navigate to="/" replace />} />
        <Route path="/oyuncular/:playerId" element={isPredictionLeague ? <FootballPlayerProfile /> : <Navigate to="/" replace />} />
        <Route path="/oyuncular/:playerId/:startYear" element={isPredictionLeague ? <FootballPlayerProfile /> : <Navigate to="/" replace />} />
        <Route path="/oyuncular/:playerId/:startYear/pozisyon/:positionCode" element={isPredictionLeague ? <FootballPlayerPositionMatches /> : <Navigate to="/" replace />} />
         <Route path="/oyuncu-karsilastirma" element={isPredictionLeague ? <FootballPlayerComparison /> : <Navigate to="/" replace />} />
         <Route path="/power-99-degisim" element={isPredictionLeague ? <FootballPowerMovers /> : <Navigate to="/" replace />} />
        <Route path="/oyuncu-liderleri" element={isPredictionLeague ? <FootballPlayerLeaders /> : <Navigate to="/" replace />} />
        <Route path="/siralamalar" element={isPredictionLeague ? <UefaRankings /> : <Navigate to="/" replace />} />
        <Route path="/oyunlar" element={isPredictionLeague ? <TakofyGames /> : <Navigate to="/" replace />} />
        <Route path="/site-home" element={isPredictionLeague ? <Navigate to="/" replace /> : <Home />} />
        <Route path="/hesabim" element={isPredictionLeague ? <PredictionAuth mode="login" /> : <Login />} />
        <Route path="/login" element={isPredictionLeague ? <PredictionAuth mode="login" /> : <Login />} />
        <Route path="/register" element={isPredictionLeague ? <PredictionAuth mode="register" /> : <Register />} />
        <Route path="/forgot" element={isPredictionLeague ? <PredictionAuth mode="forgot" /> : <ForgotPassword />} />
        <Route path="/reset" element={isPredictionLeague ? <PredictionAuth mode="reset" /> : <ResetPassword />} />
        <Route path="/kurallar" element={isPredictionLeague ? <TakofyLegal kind="rules" /> : <Navigate to="/" replace />} />
        <Route path="/privacy-policy" element={isPredictionLeague ? <TakofyLegal kind="privacy" /> : <PrivacyPolicy />} />
        <Route path="/kvkk" element={isPredictionLeague ? <TakofyLegal kind="kvkk" /> : <Kvkk />} />
          <Route path="/sapanca-gezilecek-yerler" element={isPredictionLeague ? <Navigate to="/" replace /> : <SapancaAttractions />} />

        {/* 🔐 Protected (COOKIE BASED) */}
        <Route element={<RequireAuth />}>
          <Route path="/fantezi" element={isPredictionLeague ? <CardFantasy /> : <Navigate to="/" replace />} />
          <Route path="/fantezi/kartlar" element={<Navigate to="/fantezi" replace />} />
          <Route path="/profil" element={isPredictionLeague ? <PredictionProfile /> : <Navigate to="/" replace />} />
          <Route element={<RequireAuth allowedRoles={["admin"]} />}>
            <Route path="/futbol-admin" element={isPredictionLeague ? <FootballAdmin /> : <Navigate to="/" replace />} />
          </Route>
          <Route path="/host" element={isPredictionLeague ? <Navigate to="/" replace /> : <HostLayout />}>
            <Route index element={<HostDashboard />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="reservations" element={<HostReservations />} />
            <Route path="profile" element={<HostProfile />} />
            <Route path="notifications" element={<HostNotifications />} />

            <Route element={<RequireAuth allowedRoles={["host", "admin"]} />}>
              <Route path="houses" element={<HostHouses />} />
              <Route path="houses/new" element={<WizardContainer />} />
              <Route path="houses/:id/edit" element={<HostEditHouse />} />
              <Route path="reports" element={<HostReports />} />
              <Route path="finance" element={<HostFinance />} />
              <Route path="templates" element={<HostTemplates />} />
              <Route path="logo" element={<HostLogoSettings />} />
              <Route path="site-settings" element={<HostSiteSettings />} />
              <Route path="google-ads" element={<HostGoogleAds />} />
              <Route path="users" element={<HostUsers />} />
            </Route>
          </Route>

          {/* Eski takvim rotası */}
          <Route path="/ev/:houseId/takvim" element={isPredictionLeague ? <Navigate to="/" replace /> : <CalendarPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes></React.Suspense></TakofyMobileShell> : <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/site-home" element={<Home />} />
        <Route path="/hesabim" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/kvkk" element={<Kvkk />} />
        <Route path="/sapanca-gezilecek-yerler" element={<SapancaAttractions />} />
        <Route element={<RequireAuth />}><Route path="/host" element={<HostLayout />}><Route index element={<HostDashboard />} /><Route path="calendar" element={<CalendarPage />} /><Route path="reservations" element={<HostReservations />} /><Route path="profile" element={<HostProfile />} /><Route path="notifications" element={<HostNotifications />} /><Route element={<RequireAuth allowedRoles={["host", "admin"]} />}><Route path="houses" element={<HostHouses />} /><Route path="houses/new" element={<WizardContainer />} /><Route path="houses/:id/edit" element={<HostEditHouse />} /><Route path="reports" element={<HostReports />} /><Route path="finance" element={<HostFinance />} /><Route path="templates" element={<HostTemplates />} /><Route path="logo" element={<HostLogoSettings />} /><Route path="site-settings" element={<HostSiteSettings />} /><Route path="google-ads" element={<HostGoogleAds />} /><Route path="users" element={<HostUsers />} /></Route></Route><Route path="/ev/:houseId/takvim" element={<CalendarPage />} /></Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>}
      {createPortal(
        <ToastContainer
          position="top-right"
          autoClose={3000}
          // hideProgressBar
          newestOnTop
          closeOnClick
          //pauseOnHover
          draggable
          theme="colored"
        />,
        document.getElementById("toast-root")
      )}

      <CookieBanner />
      {!hideSiteChrome ? <Footer /> : null}
      </React.Suspense>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <PublicSiteProvider>
        <DefaultCompanyProvider>
          <AppInner />
        </DefaultCompanyProvider>
      </PublicSiteProvider>
    </AuthProvider>
  );
}
